/**
 * Courses Page Status Logic:
 *
 * Status Definitions:
 * - Not started: 0% progress (no lessons completed)
 * - In progress: 1-99% progress (at least one lesson completed)
 * - Completed: 100% progress (all lessons completed)
 *
 * Progress Calculation:
 * - Backend calculates progress_percentage based on lesson_progress records
 * - Each completed lesson contributes to the overall percentage
 * - Frontend displays this percentage and filters accordingly
 *
 * Data Refresh:
 * - Page refetches course data when navigating back from lessons
 * - Ensures progress updates are immediately visible
 *
 * Course Card Clicks:
 * - Image/Icon: Navigate to course overview (/course/:id)
 * - Title: Navigate to course overview (/course/:id)
 * - Button: Navigate to next lesson (/lesson/:id)
 */

import React, { useState, useEffect, useMemo } from "react";
import { SurveyGateBanner } from "../../components/SurveyGateBanner";
import { useLocation } from "react-router-dom";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { useCoursesHook } from "./useCoursesHook";
import {
  useGetCurrentUserQuery,
  useGetEntitlementsQuery,
} from "../../store/api/postgresUserApi";
import { MasterCourseCard } from "../MasterCourses";
import { MasterCourseWithProgress } from "../../store/api/masterCourseApi";

// Trial-conversion "Request a call" target (mirrors TrialExpired.tsx).
// WS-E: build-time fallback points at the master Calendly link (a Cloud Run env
// would NOT reach this CRA build-time var — Codex B5). The prod learner build can
// still override via REACT_APP_TRIAL_CONVERSION_CALENDLY_URL in deploy.yml.
const TRIAL_CONVERSION_CALENDLY_URL =
  process.env.REACT_APP_TRIAL_CONVERSION_CALENDLY_URL ??
  "https://calendly.com/angus-griffin-pedagogue/meeting";

// WS-O: a decision-maker (makes-purchasing-decisions, or an unknown/null persona)
// sees the in-trial BUY CTA. A buyer MAKES or INFLUENCES purchasing decisions;
// only researchers / interested individuals are steered to "invite your
// decision-maker" instead. The backend convert gate mirrors this exactly — a
// non-buyer POST /orders/convert-trial returns 403. (Codex 2026-06-18 D2.)
const NON_BUYER_PERSONAS = ["researching-for-school", "interested-individual"];

// Master kill-switch for the SOCS Revenue Engine V1 in-trial conversion CTA.
// Default ON (plan: revenue_engine_v1_enabled defaults ON at launch); set the
// env var to 'false' to hide the CTA without a code change.
const REVENUE_ENGINE_V1_ENABLED =
  process.env.REACT_APP_FEATURE_REVENUE_ENGINE_V1_ENABLED !== "false";

const PAID_PLAN_TIERS = new Set([
  "WHOLE_SCHOOL",
  "ENTERPRISE_SCHOOL",
  "INTERNAL",
]);

// Package selection and backend prices live on App Home’s subscription page.

// Full-width banner for upcoming courses
// Status Dropdown Component
// eslint-disable-next-line no-unused-vars
const StatusDropdown = ({
  onStatusChange,
}: {
  // eslint-disable-next-line no-unused-vars
  onStatusChange: (_value: string) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selected, setSelected] = useState("All courses");

  const options = [
    { value: "all", label: "All courses", icon: "📚" },
    { value: "not-started", label: "Not started", icon: "🆕" },
    { value: "in-progress", label: "In progress", icon: "⏳" },
    { value: "completed", label: "Completed", icon: "🏆" },
  ];

  const handleSelect = (option: { value: string; label: string }) => {
    setSelected(option.label);
    onStatusChange(option.value);
    setIsOpen(false);
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="bg-green-100 text-green-700 px-3 py-1 rounded-full text-sm flex items-center gap-1 hover:bg-green-200 transition-colors"
      >
        {selected} ✓
        <svg
          className={`w-3 h-3 transition-transform ${isOpen ? "rotate-180" : ""}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 z-10">
          {options.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSelect(option)}
              className="w-full text-left px-4 py-2 hover:bg-gray-50 flex items-center gap-2 first:rounded-t-lg last:rounded-b-lg"
            >
              <span>{option.icon}</span>
              <span className="text-sm">{option.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const Courses = () => {
  const location = useLocation();
  const { masterCourses, isLoading, error, refetch } = useCoursesHook();
  const [statusFilter, setStatusFilter] = useState("all");
  const { data: currentUser } = useGetCurrentUserQuery();
  const { data: entitlements } = useGetEntitlementsQuery(undefined, {
    skip: !currentUser || currentUser.role === "demo",
  });

  const demoBanner = useMemo(() => {
    if (!currentUser || currentUser.role !== "demo") {
      return null;
    }

    let expiry: Date | null = null;
    if (currentUser.trial_expires_at) {
      expiry = new Date(currentUser.trial_expires_at);
    } else if (currentUser.first_login_at) {
      const fallback = new Date(currentUser.first_login_at);
      const defaultDays = Number(process.env.REACT_APP_DEMO_DEFAULT_DAYS || 7);
      fallback.setUTCDate(
        fallback.getUTCDate() +
          (Number.isFinite(defaultDays) ? defaultDays : 7),
      );
      expiry = fallback;
    }

    const now = new Date();
    const diffMs = expiry
      ? expiry.getTime() - now.getTime()
      : Number.POSITIVE_INFINITY;
    const daysLeft = expiry
      ? Math.max(0, Math.ceil(diffMs / (1000 * 60 * 60 * 24)))
      : null;

    return {
      message:
        "Your demo access is limited to the AI Made Practical for Educators course.",
      daysLeft,
      expired: expiry ? diffMs <= 0 : false,
    };
  }, [currentUser]);

  // In-trial conversion eligibility: a trial-org ADMIN (organisation_admin /
  // admin) whose org is still inside its trial window. This is the SOCS funnel
  // cohort (allocateTrial → organisation_admin + is_trial_org), distinct from
  // the `demo` role above. Trial-org admins carry NO profile-level
  // trial_expires_at, so we read the org's is_trial_org / subscription_valid_until.
  const { isTrialAdmin, trialDaysLeft } = useMemo(() => {
    const role = currentUser?.role;
    const adminEquivalent = role === "organisation_admin" || role === "admin";
    if (!currentUser?.is_trial_org) {
      return { isTrialAdmin: false, trialDaysLeft: null as number | null };
    }
    const validUntil = currentUser.subscription_valid_until
      ? new Date(currentUser.subscription_valid_until)
      : null;
    // Require a valid FUTURE expiry. A trial org always has
    // subscription_valid_until (allocateTrial sets it); a missing/invalid value
    // is anomalous data, so be conservative and hide the CTA rather than risk
    // showing it to a non-trial org.
    if (!validUntil || Number.isNaN(validUntil.getTime())) {
      return { isTrialAdmin: false, trialDaysLeft: null as number | null };
    }
    const diffMs = validUntil.getTime() - Date.now();
    if (diffMs <= 0) {
      // Expired trial orgs are handled by the backend TrialActiveGuard
      // (403 → TrialExpired page); don't show the in-app CTA here.
      return { isTrialAdmin: false, trialDaysLeft: 0 };
    }
    const ukFormatter = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Europe/London",
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    });
    const ukCalendarDate = (date: Date) => {
      const parts = Object.fromEntries(
        ukFormatter
          .formatToParts(date)
          .map(({ type, value }) => [type, value]),
      );
      return Date.UTC(
        Number(parts.year),
        Number(parts.month) - 1,
        Number(parts.day),
      );
    };
    const todayUk = ukCalendarDate(new Date());
    const expiryDate = ukCalendarDate(validUntil);
    return {
      isTrialAdmin: adminEquivalent,
      trialDaysLeft: Math.max(
        0,
        Math.round((expiryDate - todayUk) / (1000 * 60 * 60 * 24)),
      ),
    };
  }, [currentUser]);
  const isSampleTrial =
    entitlements?.isTrialOrg === true && entitlements.trialKind === "sample";

  // WS-O: gate the in-trial BUY CTA on the buyer persona; a non-buyer is steered
  // to invite their decision-maker instead (the server convert gate matches this).
  const isBuyer =
    isTrialAdmin &&
    (!currentUser?.buyer_type ||
      !NON_BUYER_PERSONAS.includes(currentUser.buyer_type));
  // The org admin console — where a trial admin invites their decision-maker.
  // Derived from the current host (learn.* -> admin.*) with a prod fallback.
  const adminConsoleUrl =
    typeof window !== "undefined" && window.location.origin.includes("learn.")
      ? window.location.origin.replace("learn.", "admin.")
      : "https://admin.pedagogue.io";
  // ISSUE 5: a bare admin.* link lands on a login/blank screen (no session in the
  // new tab). Route through the launchpad's /auto-handoff so it mints a single-use
  // handoff token and drops the trial admin into the admin console ALREADY
  // authenticated. The launchpad (app.*) is a VALID_AUDIENCES origin, and so is
  // admin.* — handoff create accepts the admin audience.
  const appLaunchpadUrl =
    typeof window !== "undefined" && window.location.origin.includes("learn.")
      ? window.location.origin.replace("learn.", "app.")
      : "https://app.pedagogue.io";
  const inviteDecisionMakerUrl = `${appLaunchpadUrl}/auto-handoff?return_to=${encodeURIComponent(
    adminConsoleUrl,
  )}`;

  const fetchError = error as FetchBaseQueryError | undefined;
  const errorCode =
    fetchError &&
    fetchError.status === 403 &&
    fetchError.data &&
    typeof fetchError.data === "object"
      ? ((fetchError.data as { error_code?: string;error?:string }).error_code || (fetchError.data as {error?:string}).error)
      : null;

  // Refetch courses when navigating to this page
  useEffect(() => {
    if (location.pathname === "/courses" && refetch) {
      refetch(); // Actually refetch the data
    }
  }, [location, refetch]);

  const isPaidEntitledOrg =
    currentUser?.role !== "demo" &&
    currentUser?.is_trial_org !== true &&
    entitlements?.isTrialOrg !== true &&
    !!entitlements?.planTier &&
    PAID_PLAN_TIERS.has(entitlements.planTier);
  const hasAssignedCourses = (masterCourses?.length || 0) > 0;
  const showPaidNoAssignedCoursesCopy =
    isPaidEntitledOrg && !hasAssignedCourses;

  // Filter master courses based on status.
  const filteredCourses = useMemo(() => {
    if (!masterCourses || masterCourses.length === 0) {
      return [];
    }

    let tabFiltered: MasterCourseWithProgress[] = masterCourses;

    if (statusFilter !== "all") {
      tabFiltered = tabFiltered.filter((course) => {
        const progress = course.progressPercentage || 0;

        switch (statusFilter) {
        case "not-started":
          // Course is not started if progress is exactly 0
          return progress === 0;

        case "in-progress":
          // Course is in progress if between 1-99%
          return progress > 0 && progress < 100;

        case "completed":
          // Course is completed if progress is 100% OR isCompleted flag is true
          return progress === 100 || course.isCompleted === true;

        default:
          return true;
        }
      });
    }
    return tabFiltered;
  }, [masterCourses, statusFilter]);

  if (errorCode === "licence_not_started") {
    const message=(fetchError?.data as {message?:unknown})?.message;
    return <main className="mx-auto max-w-3xl p-8"><h1 className="mb-5 font-display text-3xl font-bold">Courses</h1><section role="status" className="rounded-xl border border-blue-200 bg-blue-50 p-6"><h2 className="text-xl font-semibold">Your school is in its Setup Period</h2><p className="mt-3">{typeof message==="string"?message:"Staff access opens on your school’s Start Date."}</p><a className="mt-5 inline-block underline" href={appLaunchpadUrl}>Go to your apps</a></section></main>;
  }

  if (errorCode === "demo_expired") {
    return (
      <div className="max-w-4xl mx-auto p-8">
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-8 text-center shadow-sm">
          <h1 className="text-2xl font-bold text-amber-800 mb-2">
            Demo access has ended
          </h1>
          <p className="text-amber-700 mb-4">
            Your demo period has finished. Contact Pedagogue to extend your
            access or discuss full platform options.
          </p>
          <a
            href="mailto:hello@pedagogue.io"
            className="inline-flex items-center justify-center rounded-md bg-amber-600 px-4 py-2 text-white font-semibold hover:bg-amber-700 transition-colors"
          >
            Email hello@pedagogue.io
          </a>
        </div>
      </div>
    );
  }

  if (!isLoading && error && !errorCode) {
    return (
      <div className="max-w-3xl mx-auto p-8 text-center">
        <h1 className="text-2xl font-semibold text-gray-800 mb-3">
          Something went wrong
        </h1>
        <p className="text-gray-600">
          We couldn't load your courses. Please refresh the page or try again
          later.
        </p>
      </div>
    );
  }

  return (
    <div className="courses-container p-6">
      <SurveyGateBanner />
      {/* Page Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold mb-2 font-display">Courses</h1>
        <p className="text-gray-600">
          Improve your skills through interactive, hands-on professional
          courses.
        </p>
      </div>

      <div className="flex gap-2 mb-8 border-b pb-3">
        <div className="ml-auto flex items-center gap-2">
          <span className="text-gray-500">Status:</span>
          <StatusDropdown onStatusChange={setStatusFilter} />
        </div>
      </div>

      {demoBanner && (
        <div
          className={`mb-6 rounded-lg border ${
            demoBanner.expired
              ? "border-amber-300 bg-amber-50"
              : "border-blue-200 bg-blue-50"
          } px-5 py-4`}
        >
          <p
            className={`text-sm font-semibold ${demoBanner.expired ? "text-amber-800" : "text-blue-800"}`}
          >
            Demo account
          </p>
          <p
            className={`${demoBanner.expired ? "text-amber-700" : "text-blue-700"} text-sm`}
          >
            {demoBanner.message}
          </p>
          {demoBanner.daysLeft !== null && !demoBanner.expired && (
            <p className="text-sm text-gray-600 mt-1">
              {demoBanner.daysLeft > 0
                ? `You have ${demoBanner.daysLeft} day${demoBanner.daysLeft === 1 ? "" : "s"} remaining.`
                : "This is your last day—make the most of it!"}
            </p>
          )}
        </div>
      )}

      {currentUser?.is_trial_org === true && trialDaysLeft !== null && (
        <div className="mb-6 rounded-lg border border-blue-200 bg-blue-50 px-5 py-4">
          <p className="text-sm font-semibold text-blue-900">Trial</p>
          <p className="text-sm text-blue-800 mt-1">
            {trialDaysLeft > 0
              ? `Your trial ends in ${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"}`
              : "Your trial ends today"}
          </p>
        </div>
      )}

      {/* In-trial conversion CTA (SOCS Revenue Engine V1) — shown to a TRIAL-ORG
          ADMIN (organisation_admin of an is_trial_org org), the cohort the SOCS
          funnel provisions via allocateTrial. NOT the `demo` role (that is a
          separate single-course self-serve experience). */}
      {REVENUE_ENGINE_V1_ENABLED && isTrialAdmin && !isSampleTrial && (
        <div className="mb-6 rounded-lg border border-green-200 bg-green-50 px-5 py-4">
          <p className="text-sm font-semibold text-gray-900">
            Rollout across your school
          </p>
          <p className="text-sm text-gray-600 mt-1">
            Choose the package that fits your school and staff allowance, with annual
            or monthly payments over a fixed 12-month term.
          </p>
          {trialDaysLeft !== null && (
            <p className="text-sm text-gray-600 mt-1">
              {trialDaysLeft > 0
                ? `${trialDaysLeft} day${trialDaysLeft === 1 ? "" : "s"} left on your trial.`
                : "Your trial ends today."}
            </p>
          )}
          {!isBuyer && (
            <p className="text-sm text-gray-600 mt-1">
              Your school's decision-maker can purchase the rollout — invite
              them to your admin console to get started.
            </p>
          )}
          <div className="mt-3 flex flex-wrap items-center gap-4">
            {isBuyer ? (
              <a
                href={`${process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io"}/subscription`}
                className="px-5 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors"
              >
                Continue
              </a>
            ) : (
              <a
                href={inviteDecisionMakerUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="px-5 py-2 bg-green-500 text-white font-semibold rounded-lg hover:bg-green-600 transition-colors"
              >
                Invite your decision-maker
              </a>
            )}
            <a
              href={TRIAL_CONVERSION_CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm font-medium text-green-700 hover:text-green-800 underline"
            >
              Request a call
            </a>
          </div>
        </div>
      )}

      {/* Courses Grid */}
      {isLoading ? (
        <div className="text-center py-12">Loading courses...</div>
      ) : filteredCourses.length > 0 ? (
        // Show Master Course cards using the MasterCourseCard component
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {filteredCourses.map((masterCourse) => (
            <MasterCourseCard
              key={masterCourse.id}
              masterCourse={masterCourse}
              organisationLogoUrl={currentUser?.organisation_logo_url ?? null}
              showCertifiedBadge={currentUser?.role === "demo" || (!!entitlements && !isSampleTrial)}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12 text-gray-500">
          {showPaidNoAssignedCoursesCopy ? (
            <>
              <p>No courses are assigned to your account yet.</p>
              <p className="text-sm mt-2">
                Contact your school administrator or email hello@pedagogue.io if
                you expected access.
              </p>
            </>
          ) : (
            <>
              <p>No courses available in this category yet.</p>
              <p className="text-sm mt-2">Check back soon for new content!</p>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default Courses;
